import React, { useState, useEffect } from "react";
import "./ContactUs.scss";
import headimg from "../../assets/ContactUs/Group 126.png";
import Select from "react-select";
import { useLenis } from "@studio-freight/react-lenis";

interface IContactUsProps {
    openFormModal: (form: string) => void;
    children: any;
}

interface FormData {
    userType: string;
    otherType?: string;
    firstName: string;
    lastName: string;
    companyName?: string;
    companyWebsite?: string;
    shopLocations?: string;
    country: string;
    phone?: string;
    email: string;
    hearAboutUs: string;
    subject?: string;
    message?: string;
    industry?: string[];
    businessModel?: string;
}

const API_BASE_URL = "https://main-inbound-automation.vercel.app/api/contact";
const BRAND = "naarintl";

const ContactUs: React.FC<IContactUsProps> = ({ openFormModal, children }) => {
    const [currentStep, setCurrentStep] = useState<"step1" | "step2" | "step3" | "consumerResponse" | "thankYou">("step1");
    const [formData, setFormData] = useState<Partial<FormData>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [consumerResponseHtml, setConsumerResponseHtml] = useState<string>("");
    const [errors, setErrors] = useState<Record<string, string>>({});
    const lenis = useLenis();

    // Refresh Lenis when step changes to recalculate scroll height
    useEffect(() => {
        if (lenis) {
            // Small delay to ensure DOM has updated
            setTimeout(() => {
                lenis.resize();
            }, 100);
        }
    }, [currentStep, lenis]);

    const countries = [
        "Afghanistan",
        "Albania",
        "Algeria",
        "American Samoa",
        "Andorra",
        "Angola",
        "Anguilla",
        "Antarctica",
        "Antigua and Barbuda",
        "Armenia",
        "Aruba",
        "Asia/Pacific Region",
        "Australia",
        "Austria",
        "Azerbaijan",
        "Åland Islands",
        "Bahamas",
        "Bahrain",
        "Bangladesh",
        "Barbados",
        "Belarus",
        "Belgium",
        "Belize",
        "Benin",
        "Bermuda",
        "Bhutan",
        "Bolivia",
        "Bosnia and Herzegovina",
        "Botswana",
        "Bouvet Island",
        "Brazil",
        "British Indian Ocean Territory",
        "British Virgin Islands",
        "Brunei",
        "Bulgaria",
        "Burkina Faso",
        "Burundi",
        "Cambodia",
        "Cameroon",
        "Canada",
        "Canary Islands",
        "Cape Verde",
        "Caribbean Netherlands",
        "Cayman Islands",
        "Central African Republic",
        "Chad",
        "Chile",
        "China",
        "Christmas Island",
        "Cocos (Keeling) Islands",
        "Colombia",
        "Comoros",
        "Congo",
        "Costa Rica",
        "Cote d'Ivoire",
        "Croatia",
        "Cuba",
        "Curaçao",
        "Cyprus",
        "Czech Republic",
        "Democratic Republic of the Congo",
        "Denmark",
        "Djibouti",
        "Dominica",
        "Dominican Republic",
        "East Timor",
        "Ecuador",
        "Egypt",
        "El Salvador",
        "Equatorial Guinea",
        "Eritrea",
        "Estonia",
        "Ethiopia",
        "Europe",
        "Falkland Islands",
        "Faroe Islands",
        "Fiji",
        "Finland",
        "France",
        "French Guiana",
        "French Polynesia",
        "French Southern and Antarctic Lands",
        "Gabon",
        "Gambia",
        "Georgia",
        "Germany",
        "Ghana",
        "Gibraltar",
        "Greece",
        "Greenland",
        "Grenada",
        "Guadeloupe",
        "Guam",
        "Guatemala",
        "Guernsey",
        "Guinea",
        "Guinea-Bissau",
        "Guyana",
        "Haiti",
        "Heard Island and McDonald Islands",
        "Honduras",
        "Hong Kong",
        "Hungary",
        "Iceland",
        "India",
        "Indonesia",
        "Iran",
        "Iraq",
        "Ireland",
        "Isle of Man",
        "Israel",
        "Italy",
        "Jamaica",
        "Japan",
        "Jersey",
        "Jordan",
        "Kazakhstan",
        "Kenya",
        "Kiribati",
        "Kosovo",
        "Kuwait",
        "Kyrgyzstan",
        "Laos",
        "Latvia",
        "Lebanon",
        "Lesotho",
        "Liberia",
        "Libya",
        "Liechtenstein",
        "Lithuania",
        "Luxembourg",
        "Macau",
        "Madagascar",
        "Malawi",
        "Malaysia",
        "Maldives",
        "Mali",
        "Malta",
        "Marshall Islands",
        "Martinique",
        "Mauritania",
        "Mauritius",
        "Mayotte",
        "Mexico",
        "Micronesia",
        "Moldova",
        "Monaco",
        "Mongolia",
        "Montenegro",
        "Montserrat",
        "Morocco",
        "Mozambique",
        "Myanmar (Burma)",
        "Macedonia (FYROM)",
        "Namibia",
        "Nauru",
        "Nepal",
        "Netherlands",
        "Netherlands Antilles",
        "New Caledonia",
        "New Zealand",
        "Nicaragua",
        "Niger",
        "Nigeria",
        "Niue",
        "Norfolk Island",
        "North Korea",
        "Northern Mariana Islands",
        "Norway",
        "Oman",
        "Pakistan",
        "Palau",
        "Palestine",
        "Panama",
        "Papua New Guinea",
        "Paraguay",
        "Peru",
        "Philippines",
        "Pitcairn Islands",
        "Poland",
        "Portugal",
        "Puerto Rico",
        "Qatar",
        "Réunion",
        "Romania",
        "Russia",
        "Rwanda",
        "Saint Barthélemy",
        "Saint Helena",
        "Saint Kitts and Nevis",
        "Saint Lucia",
        "Saint Martin",
        "Saint Pierre and Miquelon",
        "Saint Vincent and the Grenadines",
        "Samoa",
        "San Marino",
        "Sao Tome and Principe",
        "Saudi Arabia",
        "Senegal",
        "Serbia",
        "Seychelles",
        "Sierra Leone",
        "Singapore",
        "Sint Maarten",
        "Slovakia",
        "Slovenia",
        "Solomon Islands",
        "Somalia",
        "South Africa",
        "South Georgia and the South Sandwich Islands",
        "South Korea",
        "South Sudan",
        "Spain",
        "Sri Lanka",
        "Sudan",
        "Suriname",
        "Svalbard and Jan Mayen",
        "Swaziland",
        "Sweden",
        "Switzerland",
        "Syria",
        "Taiwan",
        "Tajikistan",
        "Tanzania",
        "Thailand",
        "Togo",
        "Tokelau",
        "Tonga",
        "Trinidad and Tobago",
        "Tunisia",
        "Türkiye",
        "Turkmenistan",
        "Turks and Caicos Islands",
        "Tuvalu",
        "Uganda",
        "Ukraine",
        "United Arab Emirates",
        "United Kingdom",
        "United States",
        "United States Minor Outlying Islands",
        "Uruguay",
        "Uzbekistan",
        "U.S. Virgin Islands",
        "Vanuatu",
        "Vatican City",
        "Venezuela",
        "Vietnam",
        "Wallis and Futuna",
        "Western Sahara",
        "Yemen",
        "Zambia",
        "Zimbabwe",
    ];

    const countryOptions = countries.map((c) => ({ label: c, value: c }));

    const validateStep = (step: number): boolean => {
        const newErrors: Record<string, string> = {};

        if (step === 1) {
            if (!formData.userType) {
                newErrors.userType = "Please select a user type";
            }
            if (formData.userType === "Other" && !formData.otherType) {
                newErrors.otherType = "Please specify your type";
            }
        }

        if (step === 2) {
            if (!formData.firstName) newErrors.firstName = "First name is required";
            if (!formData.lastName) newErrors.lastName = "Last name is required";
            if (!formData.email) newErrors.email = "Email is required";
            else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Invalid email format";
            if (!formData.country) newErrors.country = "Country is required";
            if (!formData.hearAboutUs) newErrors.hearAboutUs = "This field is required";

            if (formData.userType === "Distributor/Wholesaler" || formData.userType === "Shop/Retailer") {
                if (!formData.companyName) newErrors.companyName = "Company name is required";
            }
            if (formData.userType === "Shop/Retailer" && !formData.shopLocations) {
                newErrors.shopLocations = "Number of locations is required";
            }
            if (!formData.subject && formData.userType !== "Other") {
                newErrors.subject = "Please select how we can help you";
            }
            if ((formData.subject === "Other" || formData.userType === "Other") && !formData.message) {
                newErrors.message = "Message is required";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleStep1Next = () => {
        if (validateStep(1)) {
            setCurrentStep("step2");
        }
    };

    const handleStep2Next = () => {
        if (validateStep(2)) {
            if (formData.userType === "Distributor/Wholesaler" || formData.userType === "Shop/Retailer") {
                setCurrentStep("step3");
            } else {
                handleFinalSubmit();
            }
        }
    };

    const handleFinalSubmit = async () => {
        setIsSubmitting(true);
        setErrors({}); // Clear any existing errors

        try {
            const response = await fetch(API_BASE_URL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    brand: BRAND,
                    ...formData,
                    industry: formData.industry?.join("; "),
                }),
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.message || "Submission failed");
            }

            if (formData.userType === "Consumer" && result.consumerResponse) {
                setConsumerResponseHtml(result.consumerResponse);
                setCurrentStep("consumerResponse");
            } else {
                setCurrentStep("thankYou");
            }
        } catch (error) {
            setIsSubmitting(false); // Re-enable form on error
            alert("There was an error submitting your form. Please email us at hello@naarinternational.com");
            console.error(error);
        }
        // Note: Don't set setIsSubmitting(false) on success - keep form disabled
    };

    const selectStyles = {
        container: (base: any) => ({
            ...base,
            width: "100%",
        }),
        control: (base: any) => ({
            ...base,
            backgroundColor: "rgb(67, 67, 67)",
            borderColor: "rgb(67, 67, 67)",
            borderRadius: "8px",
            minHeight: "45px",
            color: "#fff",
            fontFamily: "RedHat",
            fontSize: "16px",
            width: "100%",
            boxSizing: "border-box",
        }),
        singleValue: (base: any) => ({
            ...base,
            color: "#fff",
        }),
        input: (base: any) => ({
            ...base,
            color: "#fff",
        }),
        placeholder: (base: any) => ({
            ...base,
            color: "rgba(255, 255, 255, 0.5)",
        }),
        menu: (base: any) => ({
            ...base,
            backgroundColor: "rgb(67, 67, 67)",
            color: "#fff",
        }),
        option: (base: any, state: any) => ({
            ...base,
            backgroundColor: state.isFocused ? "rgb(100, 100, 100)" : "rgb(67, 67, 67)",
            color: "#fff",
            cursor: "pointer",
        }),
    };

    return (
        <section className="contact__us__container">
            <div className="content__container">
                <img className="contact__img" src={headimg} alt="" />
                <div className="contact__us__header">Contact Us</div>

                <div className="contact__form__wrapper">
                    <form className={`contact__form ${isSubmitting ? "form__submitting" : ""}`} onSubmit={(e) => e.preventDefault()}>
                        {/* Step 1: User Type Selection */}
                        {currentStep === "step1" && (
                            <div className="form__step">
                                <div className="form__group">
                                    <label className="form__label">I am a:</label>
                                    <div className="radio__group">
                                        {["Distributor/Wholesaler", "Shop/Retailer", "Consumer", "Other"].map((type) => (
                                            <label key={type} className="radio__label">
                                                <input type="radio" name="userType" value={type} checked={formData.userType === type} onChange={(e) => setFormData({ ...formData, userType: e.target.value })} />
                                                <span>{type}</span>
                                            </label>
                                        ))}
                                    </div>
                                    {errors.userType && <span className="error__text">{errors.userType}</span>}
                                </div>

                                {formData.userType === "Other" && (
                                    <div className="form__group">
                                        <label className="form__label">Please specify:</label>
                                        <select className="form__select" value={formData.otherType || ""} onChange={(e) => setFormData({ ...formData, otherType: e.target.value })}>
                                            <option value="">Select</option>
                                            <option value="Artist">Artist</option>
                                            <option value="Influencer">Influencer</option>
                                            <option value="Brand">Brand</option>
                                            <option value="Agency">Agency</option>
                                            <option value="Event Organizer">Event Organizer</option>
                                            <option value="Other">Other</option>
                                        </select>
                                        {errors.otherType && <span className="error__text">{errors.otherType}</span>}
                                    </div>
                                )}

                                <button type="button" className="form__button" onClick={handleStep1Next}>
                                    Next
                                </button>
                            </div>
                        )}

                        {/* Step 2: Personal Info */}
                        {currentStep === "step2" && (
                            <div className="form__step">
                                <div className="form__row">
                                    <div className="form__group">
                                        <label className="form__label">First Name *</label>
                                        <input type="text" className="form__input" value={formData.firstName || ""} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} />
                                        {errors.firstName && <span className="error__text">{errors.firstName}</span>}
                                    </div>
                                    <div className="form__group">
                                        <label className="form__label">Last Name *</label>
                                        <input type="text" className="form__input" value={formData.lastName || ""} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} />
                                        {errors.lastName && <span className="error__text">{errors.lastName}</span>}
                                    </div>
                                </div>

                                {(formData.userType === "Distributor/Wholesaler" || formData.userType === "Shop/Retailer" || formData.userType === "Other") && (
                                    <div className="form__row">
                                        <div className="form__group">
                                            <label className="form__label">Company Name {(formData.userType === "Distributor/Wholesaler" || formData.userType === "Shop/Retailer") && "*"}</label>
                                            <input type="text" className="form__input" value={formData.companyName || ""} onChange={(e) => setFormData({ ...formData, companyName: e.target.value })} />
                                            {errors.companyName && <span className="error__text">{errors.companyName}</span>}
                                        </div>
                                        <div className="form__group">
                                            <label className="form__label">Company Website</label>
                                            <input type="text" className="form__input" value={formData.companyWebsite || ""} onChange={(e) => setFormData({ ...formData, companyWebsite: e.target.value })} />
                                        </div>
                                    </div>
                                )}

                                {formData.userType === "Shop/Retailer" && (
                                    <div className="form__group">
                                        <label className="form__label">Number of locations *</label>
                                        <select className="form__select" value={formData.shopLocations || ""} onChange={(e) => setFormData({ ...formData, shopLocations: e.target.value })}>
                                            <option value="">Select</option>
                                            <option value="1">1</option>
                                            <option value="2-10">2-10</option>
                                            <option value="11-49">11-49</option>
                                            <option value="50+">50+</option>
                                            <option value="Online">Online</option>
                                        </select>
                                        {errors.shopLocations && <span className="error__text">{errors.shopLocations}</span>}
                                    </div>
                                )}

                                <div className="form__group">
                                    <label className="form__label">Country *</label>
                                    <Select options={countryOptions} value={countryOptions.find((c) => c.value === formData.country)} onChange={(option) => setFormData({ ...formData, country: option?.value || "" })} styles={selectStyles} placeholder="Select country..." />
                                    {errors.country && <span className="error__text">{errors.country}</span>}
                                </div>

                                <div className="form__row">
                                    <div className="form__group">
                                        <label className="form__label">Mobile Phone</label>
                                        <input type="text" className="form__input" value={formData.phone || ""} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                                    </div>
                                    <div className="form__group">
                                        <label className="form__label">Email *</label>
                                        <input type="email" className="form__input" value={formData.email || ""} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                                        {errors.email && <span className="error__text">{errors.email}</span>}
                                    </div>
                                </div>

                                <div className="form__group">
                                    <label className="form__label">How did you hear about us? *</label>
                                    <select className="form__select" value={formData.hearAboutUs || ""} onChange={(e) => setFormData({ ...formData, hearAboutUs: e.target.value })}>
                                        <option value="">Select</option>
                                        <option value="I already buy NAAR">I already buy NAAR</option>
                                        <option value="I saw NAAR in a shop">I saw NAAR in a shop</option>
                                        <option value="I received a client request for NAAR">I received a client request for NAAR</option>
                                        <option value="Word of mouth (friend, family, etc.)">Word of mouth (friend, family, etc.)</option>
                                        <option value="Social media">Social media</option>
                                        <option value="Online (Google, B2B platforms, etc.)">Online (Google, B2B platforms, etc.)</option>
                                        <option value="Trade show">Trade show</option>
                                    </select>
                                    {errors.hearAboutUs && <span className="error__text">{errors.hearAboutUs}</span>}
                                </div>

                                {formData.userType === "Consumer" && (
                                    <div className="form__group">
                                        <label className="form__label">How can we help you? *</label>
                                        <select className="form__select" value={formData.subject || ""} onChange={(e) => setFormData({ ...formData, subject: e.target.value })}>
                                            <option value="">Select</option>
                                            <option value="Review">Review</option>
                                            <optgroup label="After-Sales Support">
                                                <option value="How to refill my NAAR jet lighter?">How to refill my NAAR jet lighter?</option>
                                                <option value="Do you sell replacement parts?">Do you sell replacement parts?</option>
                                                <option value="My lighter is not sparking">My lighter is not sparking</option>
                                                <option value="Fuel is leaking from my lighter">Fuel is leaking from my lighter</option>
                                            </optgroup>
                                            <optgroup label="Where to Find">
                                                <option value="Where can I buy NAAR jet lighters?">Where can I buy NAAR jet lighters?</option>
                                                <option value="I am looking for a specific design">I am looking for a specific design</option>
                                            </optgroup>
                                            <option value="Custom Lighters">Custom Lighters</option>
                                            <option value="Other">Other</option>
                                        </select>
                                        {errors.subject && <span className="error__text">{errors.subject}</span>}
                                    </div>
                                )}

                                {formData.userType === "Distributor/Wholesaler" && (
                                    <div className="form__group">
                                        <label className="form__label">How can we help you? *</label>
                                        <select className="form__select" value={formData.subject || ""} onChange={(e) => setFormData({ ...formData, subject: e.target.value })}>
                                            <option value="">Select</option>
                                            <option value="Purchasing request">Purchasing request</option>
                                            <option value="Customized lighters request">Customized lighters request</option>
                                            <option value="Marketing material request">Marketing material request</option>
                                            <option value="Other">Other</option>
                                        </select>
                                        {errors.subject && <span className="error__text">{errors.subject}</span>}
                                    </div>
                                )}

                                {formData.userType === "Shop/Retailer" && (
                                    <div className="form__group">
                                        <label className="form__label">How can we help you? *</label>
                                        <select className="form__select" value={formData.subject || ""} onChange={(e) => setFormData({ ...formData, subject: e.target.value })}>
                                            <option value="">Select</option>
                                            <option value="Purchasing request">Purchasing for my shop</option>
                                            <option value="Customized lighters request">Customized lighters request</option>
                                            <option value="Marketing material request">Marketing material request</option>
                                            <option value="Other">Other</option>
                                        </select>
                                        {errors.subject && <span className="error__text">{errors.subject}</span>}
                                    </div>
                                )}

                                {(formData.subject === "Other" || formData.userType === "Other") && (
                                    <div className="form__group">
                                        <label className="form__label">Message *</label>
                                        <textarea className="form__textarea" rows={4} value={formData.message || ""} onChange={(e) => setFormData({ ...formData, message: e.target.value })} />
                                        {errors.message && <span className="error__text">{errors.message}</span>}
                                    </div>
                                )}

                                <button type="button" className="form__button" onClick={handleStep2Next} disabled={isSubmitting}>
                                    {isSubmitting ? "Submitting..." : "Next"}
                                </button>
                            </div>
                        )}

                        {/* Step 3: Business Details */}
                        {currentStep === "step3" && (
                            <div className="form__step">
                                <h3 className="form__subtitle">You're almost done</h3>
                                <p className="form__description">To save you time when we reach out, please let us know the below.</p>

                                <div className="form__group">
                                    <label className="form__label">Industry (Tick all that apply)</label>
                                    <div className="checkbox__group">
                                        {["FMCG", "Smoking / Tobacco", "Food Products", "Beverages & Spirits", "Generalist", "Other"].map((industry) => (
                                            <label key={industry} className="checkbox__label">
                                                <input
                                                    type="checkbox"
                                                    value={industry}
                                                    checked={formData.industry?.includes(industry)}
                                                    onChange={(e) => {
                                                        const current = formData.industry || [];
                                                        if (e.target.checked) {
                                                            setFormData({ ...formData, industry: [...current, industry] });
                                                        } else {
                                                            setFormData({ ...formData, industry: current.filter((i) => i !== industry) });
                                                        }
                                                    }}
                                                />
                                                <span>{industry}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {formData.userType === "Distributor/Wholesaler" && (
                                    <div className="form__group">
                                        <label className="form__label">Business Model</label>
                                        <div className="radio__group">
                                            {["Direct distribution to retail shops", "Distribution through wholesalers", "Van jobber", "Cash and carry facility", "Trading company"].map((model) => (
                                                <label key={model} className="radio__label">
                                                    <input type="radio" name="businessModel" value={`B2B - ${model}`} checked={formData.businessModel === `B2B - ${model}`} onChange={(e) => setFormData({ ...formData, businessModel: e.target.value })} />
                                                    <span>{model}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {formData.userType === "Shop/Retailer" && (
                                    <div className="form__group">
                                        <label className="form__label">Business Model</label>
                                        <div className="radio__group">
                                            {["Convenience store", "Supermarket", "Tobacco/Smoke/Vape Shop", "Dispensary/Cannabis Store", "Other (Souvenir, Hotel, Restaurant, Tattoo, etc.)"].map((model) => (
                                                <label key={model} className="radio__label">
                                                    <input type="radio" name="businessModel" value={`B2C - ${model}`} checked={formData.businessModel === `B2C - ${model}`} onChange={(e) => setFormData({ ...formData, businessModel: e.target.value })} />
                                                    <span>{model}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <button type="button" className="form__button" onClick={handleFinalSubmit} disabled={isSubmitting}>
                                    {isSubmitting ? "Submitting..." : "Submit"}
                                </button>
                            </div>
                        )}

                        {/* Consumer Response */}
                        {currentStep === "consumerResponse" && (
                            <div className="form__step">
                                <div className="consumer__response" dangerouslySetInnerHTML={{ __html: consumerResponseHtml }} />
                                <button type="button" className="form__button" onClick={() => setCurrentStep("thankYou")}>
                                    Done
                                </button>
                            </div>
                        )}

                        {/* Thank You */}
                        {currentStep === "thankYou" && (
                            <div className="form__step thank__you">
                                <h3 className="form__title">Thank you.</h3>
                                <p className="form__description">We will get back to you as soon as possible.</p>

                                {formData.country === "United States" && (formData.userType === "Distributor/Wholesaler" || formData.userType === "Shop/Retailer") && (
                                    <div className="shop__link__section">
                                        <h3 className="form__subtitle">Explore Our US Shop!</h3>
                                        <p className="form__description">Check out our products and checkout on our wholesale shop.</p>
                                        <a href="https://www.naarusa.com/shop" target="_blank" rel="noreferrer" className="form__button">
                                            SHOP
                                        </a>
                                    </div>
                                )}
                            </div>
                        )}
                    </form>
                  </div>
            </div>
            {children}
        </section>
    );
};

export default ContactUs;
